// websocket/combat-lifecycle-truth.ts — R-119 (Janus lane N5, 2026-10-01): the AUTO-EXIT, decided by the page that saw the fight end.
//
// Replaces the server's ft_queue_encounter_combat_exit_check() → shutdown flush → ft_auto_exit_combat_if_last_combatant_standing() (all
// deleted). Every page holding an encounter watches its held fight (janus-engine/combat/opposition.ts — edge-triggered: a LIVING member
// left — dead, deleted, gone from the roster, untagged — and no friend / enemy pair is left standing). A removal opens a PENDING decision
// for that fight; every further change to the held encounter restarts its settle window (SETTLE_MS of quiet), and at the end the page
// re-reads: the same fight, still in combat, the removal still standing (a death undone in the same action — a resurrection, a replacement
// row — cancels it), no opposition, every team readable. Only then is the exit posted through the combat-truth lane (`tryCommitAutoExit`:
// the R-118 exit plan with reason `no_opposition`, its own R-115 refill, one deterministic operation id per (encounter, combat) so every
// page that saw it lands on one receipt; the lane abandons it if the held fight changed while it planned). Who may post: the encounter's
// GM, or a player who owns a member of that fight (the endpoint checks it from the rows) — so no GM page needs to be open when the last
// foe falls. Top window only (the FT3D scene iframe loads this bundle too).
// The turn-holder death (Session A2 step 4c, resolveTurnDeath / turn-death-registry.ts) needs no hook here: this watcher reads the state
// after A2's transition frame applied, whichever way the pointer went, and the settle window covers the frames of one action.
import { autoExitVerdict, combatSides, type FightMemory } from '../janus-engine/combat/opposition.js';
import { tryCommitAutoExit } from './combat-truth.js';
import { orbBoundaryWork, orbPassSettledFor } from './orb-truth.js';
import { normalizeOrbQueue } from '../janus-engine/ability/orbs/orb-queue.js';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { departureBoundaryWork, observeServerDeparture } from './server-departure-truth.js';
import { withAsyncDeadline } from '../shared/async-deadline.js';
import { COMBAT_IDLE_MS } from '../shared/combat-settlement.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const SETTLE_MS = 1500;
/** A16 (CE2): how long a page waits for its OWN owed orb pass, and the grace a page owing none gives an orb-holding fight's owing page. */
// The peer grace OUTLASTS the owing page's own wait (closing review P2): a peer never ends the fight while the owing page may still land its pass.
const ORB_OWN_WAIT_MS = 30000, ORB_PEER_GRACE_MS = ORB_OWN_WAIT_MS + 5000;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

interface Pending { combatId: string; removed: Set<string>; timer: ReturnType<typeof setTimeout> | null; firstSettleAt?: number; openedAt?: number }
const memory = new Map<string, FightMemory>();
const pending = new Map<string, Pending>();
const inFlight = new Set<string>();
const settled = new Set<string>();
const emit = (name: string, detail: unknown): void => { window.dispatchEvent(new CustomEvent(name, { detail })); };

function encounterFor(id: string): Row | null { return asRow(window.FateTide?.encounterCache?.get?.(id) ?? window.FateTide?.encounterCache?.get?.(upper(id))); }
function campaignOf(encounter: Row | null): string {
  const bucket = asRow(encounter?.encounters);
  return upper(bucket && 'encounter_master_parent_id' in bucket ? bucket.encounter_master_parent_id : encounter?.encounter_master_parent_id);
}

/** The endpoint's own authority rule, pre-checked here so a page that may not post never tries: the GM, or the owner of a member row of
 * this fight (dead or alive) or of a member's controller. */
export function mayPostAutoExit(encounter: Row, combatId: string, user: { id?: string; isGM?: boolean } | undefined): boolean {
  if (user?.isGM === true) return true;
  const uid = upper(user?.id);
  if (!uid) return false;
  const rows = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow).filter((r): r is Row => !!r);
  const ec = (r: Row): Row => asRow(r.encounter_creatures) ?? {};
  const byId = new Map(rows.map(r => [upper(ec(r).encounter_creature_id), r]));
  const owns = (r: Row | undefined): boolean => !!r && upper(ec(r).encounter_creature_created_by_id) === uid;
  return rows.some(r => (!combatId || upper(ec(r).combat_id) === combatId) && Number(scalar(ec(r).encounter_creature_deleted) ?? 0) === 0
    && (owns(r) || owns(byId.get(upper(ec(r).controller_encounter_creature_id)))));
}

/** A REMOVED member of this fight still holding channelled orbs — its last boundary may owe a pass on another page. */
/** Round 35 P2: the grace is owed per BOUNDARY — a removed holder whose ended turn's pass THIS page has not settled since the death decision opened
 *  (the holder's turn ended in the current round, or in the one before when its end wrapped). An earlier turn's pass never stands in. */
function unsettledOrbHolderDied(encounterId: string, encounter: Row, p: Pending): boolean {
  const rows = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow).filter((r): r is Row => !!r);
  const round = Number(scalar(asRow(encounter.encounters)?.current_round ?? encounter.current_round));
  const rounds = Number.isInteger(round) ? [round, round - 1] : [];
  return rows.some(r => {
    const ec = asRow(r.encounter_creatures) ?? {};
    const id = upper(ec.encounter_creature_id);
    return p.removed.has(id) && (!p.combatId || upper(ec.combat_id) === p.combatId)
      && (normalizeOrbQueue(scalar(ec.orb_queue)) ?? []).length > 0
      && !orbPassSettledFor(encounterId, p.combatId, rounds, id, p.openedAt ?? 0);
  });
}

function arm(id: string, p: Pending): void {
  if (p.timer) clearTimeout(p.timer);
  p.timer = setTimeout(() => { p.timer = null; void settle(id).catch(error => console.warn('[combat-lifecycle] auto-exit failed', error)); }, SETTLE_MS);
}

async function settle(encounterId: string): Promise<void> {
  const p = pending.get(encounterId);
  if (!p) return;
  // A server-origin departure's START/clock work precedes the empty-fight exit.
  await departureBoundaryWork(encounterId);
  const encounter = encounterFor(encounterId);
  const sides = combatSides(encounter);
  const skip = (why: string): void => { pending.delete(encounterId); emit('ft:combat-auto-exit-skipped', { encounterId, combatId: p.combatId, why }); };
  // Re-read after the action settled: the same fight, still in combat, readable, the removal still standing, still no opposition.
  if (!encounter || sides.inCombat !== true || sides.combatId !== p.combatId) return skip('fight-changed');
  if (sides.unknown || sides.unknownTeam.length) return; // keep pending: the next readable change re-arms it
  const living = new Set(sides.living);
  if (![...p.removed].some(r => !living.has(r))) return skip('removal-undone');
  if (sides.opposition) return skip('opposition');
  // A16 (EC1 CE2, 2026-10-09): the fight's last turn may still owe its orb pass. The pinned legacy pulsed a holder's channelled orbs INSIDE
  // end_turn, before anything could end the fight — so a holder killed at its own boundary still pulses. This page's own pass lands first
  // (orbBoundaryWork); a page that owes none but sees an orb holder in the fight gives the owing page a bounded grace before it posts. Ordering
  // only — the decision itself is unchanged; the shared receipt makes whichever page posts the one exit.
  const now = Date.now();
  p.firstSettleAt ??= now;
  const orbWork = orbBoundaryWork(encounterId);
  const waitOwn = !!orbWork && now - p.firstSettleAt < ORB_OWN_WAIT_MS;
  // The peer grace is scoped to the CE2 shape — an orb holder is among the creatures whose removal ends the fight (killed at its own boundary,
  // its pass still owed by the page that ended its turn); an ordinary end with a living holder never waits on a page that owes nothing.
  const waitPeer = !orbWork && unsettledOrbHolderDied(encounterId, encounter, p)
    && now - p.firstSettleAt < ORB_PEER_GRACE_MS;
  if (waitOwn || waitPeer) {
    emit('ft:combat-auto-exit-waiting', { encounterId, combatId: p.combatId, why: waitOwn ? 'orb-boundary-work' : 'orb-holder-grace' });
    const resume = (): void => { if (pending.get(encounterId) === p && !p.timer) arm(encounterId, p); };
    // a deadline wake-up rides the own-work promise too (closing review P2): a pass that never settles still releases the exit at the bound
    if (orbWork) { void orbWork.then(resume); setTimeout(resume, ORB_OWN_WAIT_MS - (now - p.firstSettleAt) + 50); }
    else setTimeout(resume, ORB_PEER_GRACE_MS - (now - p.firstSettleAt) + 50);
    return;
  }
  const campaignId = campaignOf(encounter);
  if (!UUID.test(campaignId) || !mayPostAutoExit(encounter, p.combatId, window.FateTide?.user)) return skip('not-this-page');
  const key = `${encounterId}:${p.combatId}`;
  if (inFlight.has(key)) return; // preserve changes that arrive while the attempt is running
  if (settled.has(key)) return skip('already-posted');
  pending.delete(encounterId);
  inFlight.add(key);
  emit('ft:combat-auto-exit-deciding', { encounterId, combatId: p.combatId, removed: [...p.removed] });
  try {
    const outcome = await withAsyncDeadline(signal => tryCommitAutoExit({ campaignId, encounterId, combatId: p.combatId, signal }), COMBAT_IDLE_MS);
    // 'handled' (committed, or settled through the shared receipt) and null (a definite refusal: another page ended it, or it was not this
    // page's to end) are both final for this fight on this page; only a throw leaves it open for the next change to retry.
    settled.add(key);
    if (settled.size > 128) settled.delete(settled.values().next().value!);
    emit('ft:combat-auto-exit-posted', { encounterId, combatId: p.combatId, removed: [...p.removed], outcome });
  } catch (error) {
    // A failed or expired attempt retains the decision and retries after a pause.
    const newer = pending.get(encounterId);
    if (!newer || newer.combatId === p.combatId) {
      if (newer?.timer) clearTimeout(newer.timer);
      const next: Pending = { ...p, removed: new Set([...p.removed, ...(newer?.removed ?? [])]), timer: null };
      pending.set(encounterId, next);
      // A failed background attempt must wake itself, even without another frame.
      next.timer = setTimeout(() => { next.timer = null; void settle(encounterId).catch(error => console.warn('[combat-lifecycle] auto-exit retry failed', error)); }, 3000);
    }
    throw error;
  } finally { inFlight.delete(key); }
}

function evaluate(encounterId: string): void {
  // A practice round must continue while its fallen dummies wait to respawn.
  if (ftCurrentTruthSink()) return;
  const id = upper(encounterId);
  if (!id || window.top !== window || !window.FateTide?.user) return;
  const encounter = encounterFor(id);
  if (!encounter) return;
  observeServerDeparture(id);
  const { verdict, memory: next } = autoExitVerdict(memory.get(id) ?? null, encounter);
  if (next) memory.set(id, next); else memory.delete(id);
  const p = pending.get(id);
  if (verdict.exit) {
    const same = p && p.combatId === verdict.combatId ? p : null;
    const fresh: Pending = same ?? { combatId: verdict.combatId, removed: new Set(), timer: null, openedAt: Date.now() };
    verdict.removed.forEach(r => fresh.removed.add(r));
    pending.set(id, fresh);
    arm(id, fresh);
    return;
  }
  if (!p) return;
  // A pending decision: the fight ended / changed → drop it; any other change restarts the settle window (the action is still landing).
  if (verdict.why === 'out-of-combat' || verdict.why === 'new-combat') { if (p.timer) clearTimeout(p.timer); pending.delete(id); return; }
  arm(id, p);
}

// Every way a held row or the held encounter changes: a full / delta encounter frame, a patched row set, a turn-state change, and any
// truth lane's caster update (attack / ability / damage / status / GM tools all end in handleCasterUpdate → ft:caster-state-applied).
for (const name of ['ft:encounter-state-received', 'ft:encounter-rows-patched', 'ft:encounter-turn-state', 'ft:caster-state-applied']) {
  window.addEventListener(name, (event: Event) => {
    try { evaluate(upper((event as CustomEvent).detail?.encounterId)); } catch (error) { console.warn('[combat-lifecycle] evaluate failed', error); }
  });
}
// The BASELINE: an encounter installed from its initial HTTP snapshot fires no change event, so without a first sight the first death
// would itself become the baseline (and never read as a removal). Take it on install, and sweep the held encounters every few seconds
// for any this page has no baseline for yet (cheap: one pure read per held encounter).
window.addEventListener('ft:encounter-installed', (event: Event) => {
  const arr = asRow((event as CustomEvent).detail?.encounterArr);
  const id = upper(asRow(arr?.encounters)?.encounter_id ?? arr?.encounter_id);
  if (id) setTimeout(() => { try { if (!memory.has(id)) evaluate(id); } catch { /* never breaks the install */ } }, 0);
});
setInterval(() => {
  try {
    const cache = window.FateTide?.encounterCache;
    if (cache instanceof Map) for (const key of cache.keys()) {
      const id = upper(key);
      if (id) observeServerDeparture(id);
      if (id && !memory.has(id) && !pending.has(id)) evaluate(id);
    }
  } catch { /* a sweep never throws into the page */ }
}, 3000);
Object.assign(window, { ftCombatAutoExitEvaluate: evaluate });
